import { useState } from "react";
import { Link } from "react-router-dom";
import { Instagram, Music2, Grip } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { BRAND } from "@/data/site";

const COLS = [
  {
    title: "Shop",
    links: [
      ["New Arrivals", "/collection/new-arrivals"],
      ["Best Sellers", "/collection/best-sellers"],
      ["Necklaces", "/collection/necklaces"],
      ["Earrings", "/collection/earrings"],
      ["Bracelets", "/collection/bracelets"],
      ["Rings", "/collection/rings"],
    ],
  },
  {
    title: "Company",
    links: [
      ["About Us", "/about"],
      ["Contact", "/contact"],
      ["FAQ", "/about"],
      ["Shipping & Returns", "/about"],
    ],
  },
  {
    title: "Legal",
    links: [
      ["Privacy Policy", "/about"],
      ["Terms & Conditions", "/about"],
    ],
  },
];

export function Footer() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);

  const subscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    setBusy(true);
    try {
      const { data } = await api.post("/newsletter", { email });
      toast.success(data.message || "Welcome to the Noora list");
      setEmail("");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Please try again");
    } finally {
      setBusy(false);
    }
  };

  return (
    <footer className="bg-ink text-ivory" data-testid="footer">
      {/* Newsletter */}
      <div className="border-b border-white/10 px-5 py-20 md:px-10">
        <div className="mx-auto grid max-w-[1400px] gap-10 md:grid-cols-2 md:items-end">
          <div>
            <p className="eyebrow text-champagne">A Little More Noora</p>
            <h2 className="mt-4 font-serif text-4xl font-light leading-tight md:text-5xl">
              Be the first to discover new collections, exclusive offers, and everyday inspiration.
            </h2>
          </div>
          <form onSubmit={subscribe} className="flex flex-col gap-3" data-testid="footer-newsletter-form">
            <div className="flex items-center gap-3 border-b border-white/25 pb-3">
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email"
                className="w-full bg-transparent text-lg outline-none placeholder:text-white/40"
                data-testid="footer-newsletter-input"
              />
            </div>
            <button
              type="submit"
              disabled={busy}
              className="mt-2 w-fit rounded-full bg-ivory px-9 py-3.5 text-[0.7rem] uppercase tracking-[0.24em] text-ink transition-colors hover:bg-champagne disabled:opacity-60"
              data-testid="footer-newsletter-submit"
            >
              {busy ? "Joining…" : "Join the Noora List"}
            </button>
          </form>
        </div>
      </div>

      {/* Links */}
      <div className="mx-auto grid max-w-[1400px] gap-12 px-5 py-16 md:grid-cols-[1.4fr_1fr_1fr_1fr] md:px-10">
        <div>
          <Link to="/" className="font-serif text-3xl tracking-[0.18em]">
            {BRAND.name}
          </Link>
          <p className="mt-5 max-w-xs text-sm leading-relaxed text-white/60">
            Beautiful anti-tarnish jewelry designed for everyday wear. Made to shine. Made to last.
          </p>
          <div className="mt-7 flex gap-4">
            <a href="https://instagram.com" target="_blank" rel="noreferrer" aria-label="Instagram" className="grid h-10 w-10 place-items-center rounded-full border border-white/15 transition-colors hover:border-champagne hover:text-champagne">
              <Instagram size={17} strokeWidth={1.5} />
            </a>
            <a href="https://tiktok.com" target="_blank" rel="noreferrer" aria-label="TikTok" className="grid h-10 w-10 place-items-center rounded-full border border-white/15 transition-colors hover:border-champagne hover:text-champagne">
              <Music2 size={17} strokeWidth={1.5} />
            </a>
            <a href="https://pinterest.com" target="_blank" rel="noreferrer" aria-label="Pinterest" className="grid h-10 w-10 place-items-center rounded-full border border-white/15 transition-colors hover:border-champagne hover:text-champagne">
              <Grip size={17} strokeWidth={1.5} />
            </a>
          </div>
        </div>
        {COLS.map((col) => (
          <div key={col.title}>
            <h4 className="eyebrow text-champagne">{col.title}</h4>
            <ul className="mt-5 flex flex-col gap-3">
              {col.links.map(([label, to]) => (
                <li key={label}>
                  <Link to={to} className="text-sm text-white/60 transition-colors hover:text-ivory">
                    {label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>

      <div className="border-t border-white/10 px-5 py-6 text-center text-xs tracking-wide text-white/40 md:px-10">
        © 2026 Noora Jewels. All rights reserved.
      </div>
    </footer>
  );
}
